import { useEffect, useState } from 'react'
import {
  FEATURE_FLAG_MODE_LABELS,
  featureFlagsApi,
  type FeatureFlag,
  type FeatureFlagMode,
} from '../api/featureFlags'
import { resetFeatureFlagCache } from '../lib/featureFlags'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  type PillTone,
  useConfirm,
  FormGrid,
} from '../components/ui'

/**
 * F-063 (TASK-0317, REQ-258-001): feature flag. L'amministratore accende una funzione nuova a gradi:
 * spenta, solo per gli admin, solo per utenti o categorie indicati, oppure per tutti. Nessun flag e'
 * preimpostato e nessuna funzione esistente dipende da un flag. La chiave non cambia dopo la creazione.
 * Pagina raggiungibile solo dall'Admin (il server risponde 403 agli altri).
 */
interface FormState {
  id?: number
  key: string
  description: string
  mode: FeatureFlagMode
  userIds: string
  userCategoryIds: string
}

const EMPTY_FORM: FormState = { key: '', description: '', mode: 'Spento', userIds: '', userCategoryIds: '' }

const MODES = Object.keys(FEATURE_FLAG_MODE_LABELS) as FeatureFlagMode[]

const MODE_TONES: Record<FeatureFlagMode, PillTone> = {
  Spento: 'neutral',
  SoloAdmin: 'info',
  Selezionati: 'warning',
  Tutti: 'success',
}

/** "3, 5 7" -> [3, 5, 7]; null se c'e' qualcosa che non e' un intero positivo. */
function parseIds(text: string): number[] | null {
  const parts = text.split(/[\s,;]+/).filter(Boolean)
  const ids = parts.map(p => (/^\d+$/.test(p) ? Number(p) : NaN))
  return ids.every(n => Number.isSafeInteger(n) && n > 0) ? ids : null
}

export function FeatureFlagsPage() {
  const askConfirm = useConfirm()
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  const isEdit = form.id !== undefined

  async function load() {
    setLoading(true)
    setError(null)
    try {
      setFlags((await featureFlagsApi.list()).items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  function openCreate() {
    setForm(EMPTY_FORM)
    setModalError(null)
    setShowModal(true)
  }

  function openEdit(f: FeatureFlag) {
    setForm({
      id: f.id,
      key: f.key,
      description: f.description ?? '',
      mode: f.mode,
      userIds: f.userIds.join(', '),
      userCategoryIds: f.userCategoryIds.join(', '),
    })
    setModalError(null)
    setShowModal(true)
  }

  function closeModal() {
    if (saving) return
    setShowModal(false)
    setModalError(null)
  }

  async function handleSave() {
    if (!isEdit && !/^[a-z0-9_.-]{3,64}$/.test(form.key))
      return setModalError('Chiave non valida: 3-64 caratteri tra minuscole, cifre, _ . -')
    const userIds = parseIds(form.userIds)
    const userCategoryIds = parseIds(form.userCategoryIds)
    if (userIds === null) return setModalError('Gli id utente devono essere numeri interi separati da virgole')
    if (userCategoryIds === null)
      return setModalError('Gli id categoria devono essere numeri interi separati da virgole')

    setSaving(true)
    setModalError(null)
    try {
      const body = {
        description: form.description.trim() || null,
        mode: form.mode,
        userIds,
        userCategoryIds,
      }
      if (isEdit) await featureFlagsApi.update(form.id as number, body)
      else await featureFlagsApi.create({ ...body, key: form.key })
      resetFeatureFlagCache()
      setShowModal(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(f: FeatureFlag) {
    if (!(await askConfirm({ title: 'Elimina flag', message: `Eliminare il flag "${f.key}"? Le funzioni che lo leggono torneranno spente.`, confirmLabel: 'Elimina', destructive: true }))) return
    setError(null)
    try {
      await featureFlagsApi.remove(f.id)
      resetFeatureFlagCache()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nell\'eliminazione')
    }
  }

  if (loading) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title="Feature flag"
        icon="toggle_on"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          <Button variant="filled" icon="add" onClick={openCreate}>
            Nuovo flag
          </Button>
        }
      />

      <Callout tone="info">
        Accendi le funzioni nuove a gradi. Un flag che non esiste vale &quot;spento&quot;; &quot;Solo utenti
        o categorie indicati&quot; non include gli amministratori se non sono nell&apos;elenco.
      </Callout>

      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined" padding="none">
        {flags.length === 0 ? (
          <EmptyState icon="toggle_off" title="Nessun flag configurato." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Chiave</th>
                <th>Descrizione</th>
                <th>Stato</th>
                <th>Utenti</th>
                <th>Categorie</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {flags.map(f => (
                <tr key={f.id}>
                  <td className="md-typescale-emphasized-body-medium"><code>{f.key}</code></td>
                  <td>{f.description ?? '—'}</td>
                  <td><StatusPill tone={MODE_TONES[f.mode]}>{FEATURE_FLAG_MODE_LABELS[f.mode]}</StatusPill></td>
                  <td>{f.userIds.length > 0 ? f.userIds.join(', ') : '—'}</td>
                  <td>{f.userCategoryIds.length > 0 ? f.userCategoryIds.join(', ') : '—'}</td>
                  <td>
                    <IconButton icon="edit" size="sm" aria-label="Modifica"
                      onClick={() => openEdit(f)} />{' '}
                    <IconButton icon="delete" size="sm" aria-label="Elimina"
                      onClick={() => void handleDelete(f)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        dismissible={!saving}
        title={isEdit ? 'Modifica flag' : 'Nuovo flag'}
        actions={
          <>
            <Button variant="text" onClick={closeModal} disabled={saving}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving}>
              {saving ? 'Salvataggio…' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}
          <FormGrid>
            <TextField className="md-span-full" id="ff-key" label="Chiave" type="text" value={form.key} disabled={isEdit}
              maxLength={64}
              supportingText={!isEdit ? 'es. nuova.dashboard. Minuscole, cifre, _ . - (3-64 caratteri). Non si cambia dopo.' : 'es. nuova.dashboard'}
              onChange={e => setForm(f => ({ ...f, key: e.target.value }))} />
            <TextField className="md-span-full" id="ff-desc" label="Descrizione" type="text" value={form.description} maxLength={500}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            <Select className="md-span-full" id="ff-mode" label="Stato" value={form.mode}
              onChange={e => setForm(f => ({ ...f, mode: e.target.value as FeatureFlagMode }))}>
              {MODES.map(m => <option key={m} value={m}>{FEATURE_FLAG_MODE_LABELS[m]}</option>)}
            </Select>
            {form.mode === 'Selezionati' && (
              <>
                <TextField id="ff-users" label="Id utente" type="text" value={form.userIds}
                  supportingText="es. 12, 34"
                  onChange={e => setForm(f => ({ ...f, userIds: e.target.value }))} />
                <TextField id="ff-cats" label="Id categoria utente" type="text" value={form.userCategoryIds}
                  supportingText="es. 3"
                  onChange={e => setForm(f => ({ ...f, userCategoryIds: e.target.value }))} />
              </>
            )}
          </FormGrid>
        </>
      </Dialog>
    </Page>
  )
}
